import React, { useEffect, useState } from "react";
// ANT DESIGN COMPONENTS
import { Drawer, Input, Select, InputNumber, Tooltip, Alert, Spin } from "antd";
// NAVIGATION
import { useLocation } from "react-router-dom";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// FONT AWESOME LIBRARY AND ICONS
import { faBuilding, faCircleInfo, faEthernet, faGears } from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useSelector, useDispatch } from "react-redux";
import { deserializeApiKeys } from "../../components/adapters/AdapterEditor";
import { updateOpenModalCreateEditBank, updateBank } from "../../../redux/banks/bankSlice";
import ConfigurationFilesAssignConfiguration from "../../configurationManagement/configurationFiles/ConfigurationFilesAssignConfiguration";
import { usePostBankMutation, usePutBankMutation } from "../../../redux/banks/bankAPI";
import { usePostLiftMutation } from "../../../redux/lifts/liftAPI";
import { useLazyGetServerQuery } from "../../../redux/servers/serverAPI";
// COMPONENTS
import DrawerRowDivider from "../../components/drawer/DrawerRowDivider";
import SelectOrganization from "../../components/select/SelectOrganization";
import SelectServerByOrganization from "../../components/select/SelectServerByOrganization";
import AdaptersAssignAdapter from "../adapters/AdaptersAssignAdapter";
import BankOffsetMatrix, {
  extractMappedMessageTypes,
  MappedMessageType,
} from "../../setupWizard/steps/BankOffsetMatrix";
import GlobalAlert2 from "../../home/GlobalAlert2";
import ButtonClose from "../../components/buttons/ButtonClose";
import ButtonSave from "../../components/buttons/ButtonSave";
import OpcVendorPathFeedback from "../../components/form/OpcVendorPathFeedback";
// INTERFACES
import { RootState } from "../../../app/store";
import { BankInterface } from "../../../interfaces/Bank.interface";
import { LiftType } from "../../../flatbuffers/generated/archive/lift-type";
import { AdapterProtocol } from "../../../flatbuffers/generated/archive/adapter-protocol";
import { liftTypeLabel, formatUuidT } from "../../../flatbuffers/display";
import { ChannelType, FormAction, ProtocolType } from "../../../types/enums";
import {
  OPC_PATH_PROTOCOLS,
  OPC_VENDOR_PATH_FIELDS,
  OPC_VENDOR_PATH_CAMEL,
  OPC_VENDOR_PATH_LABELS,
  OpcVendorPathField,
  composeOpcUaNodePreview,
  firstLiftVendorPath,
  opcVendorPathTransition,
  readOpcVendorPathValues,
} from "../../../constants/opcVendorPaths";
import { extractAdapterKeys } from "../../components/adapters/adapterMatrixInputs";
import { ChannelInterface } from "../../../interfaces/Channel.interface";
import { FloorsAndDirectionDataInterface } from "../../../interfaces/Configurations.interface";
// AUTHORIZATION
import GETJwtToken from "../../../redux/authentication/authentication";
// CONSTANTS
import { BLANK_BANK } from "../../../constants/bank";
import {
  logicalPortMax,
  logicalPortMin,
  logicalPortOutOfRange,
} from "../../../constants/bankLogicalPort";

interface BanksCreateEditFormProps {
  mode?: "drawer" | "page";
  onSaveSuccess?: () => void;
  onCancel?: () => void;
}

// ---------------------------------------------------------------------------
// Module-level helpers (mirrored from StepBanks — not exported there)
// ---------------------------------------------------------------------------

/** Protocols that use LN1-style offset_mapping + keys (no per-key AdapterKeyInfo). */

/**
 * Parse stored offset_mapping into the keyed format used by BankOffsetMatrix.
 * Handles: null/undefined → [], new keyed format, legacy positional format.
 */
function parseOffsetMapping(stored: unknown, types: MappedMessageType[]): Record<string, number>[] {
  if (!Array.isArray(stored) || stored.length === 0) return [];
  if (typeof stored[0] === "object" && stored[0] !== null && !Array.isArray(stored[0])) {
    return stored as Record<string, number>[];
  }
  // Legacy positional format: [[9, 7, 8], ...] — convert to keyed
  return (stored as number[][]).map((row) => {
    const keyed: Record<string, number> = {};
    types.forEach((mt, colIdx) => {
      keyed[mt.categoryKey] = row[colIdx] ?? 0;
    });
    return keyed;
  });
}

/**
 * Resolve an adapter's protocol to an uppercase string regardless of which
 * serialization handed it over: the JSON API surfaces a `protocol_type`
 * string, the FB Object API path a camelCase `protocolType` that can arrive
 * as either the string or the numeric enum discriminant. Mirrors the helper
 * of the same name in StepBanks (not exported there).
 */
function resolveAdapterProtocolUpper(adapter: any): string {
  const proto = adapter?.protocol_type ?? adapter?.protocolType ?? adapter?.ProtocolType;
  if (typeof proto === "string" && proto) return proto.toUpperCase();
  if (typeof proto === "number") {
    const name = AdapterProtocol[proto];
    if (typeof name === "string") return name;
  }
  return "";
}

function BanksCreateEditForm({
  mode = "drawer",
  onSaveSuccess = undefined,
  onCancel = undefined,
}: BanksCreateEditFormProps) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const dispatch = useDispatch();
  const [t] = useTranslation("global");
  const { Option } = Select;
  const location = useLocation();
  const idServer = new URLSearchParams(location.search).get("idServer");
  const BLANK_FLOORS_DIRECTION: FloorsAndDirectionDataInterface = {
    numberOfLifts: 0,
    numberOfStops: 2,
    maxNumberOfStops: 0,
    numberOfFloorsBelowLobby: 0,
    configurationId: "",
    adapterId: "",
  };

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [bank, setBank] = useState({ ...BLANK_BANK });
  const [idOrganization, setIdOrganization] = useState<string>("");
  const [channels, setChannels] = useState<ChannelInterface[]>([]);
  const [channelType, setChannelType] = useState<string>("");
  const [dataBlocks, setDataBlocks] = useState([]);
  const [floorsAndDirectionData, setFloorsAndDirectionData] =
    useState<FloorsAndDirectionDataInterface>(BLANK_FLOORS_DIRECTION);
  const [showLoading, setShowLoading] = useState<boolean>(false);
  const [offsetMatrixValues, setOffsetMatrixValues] = useState<Record<string, number>[]>([]);
  const [offsetMatrixHasOverlaps, setOffsetMatrixHasOverlaps] = useState<boolean>(false);

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const {
    openModalCreateEditBank,
    formAction,
    bank: selectedBank,
  } = useSelector((state: RootState) => state.bank);
  const { theme, userLogged, globalOrganizationId } = useSelector((state: RootState) => state.home);

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */
  const [triggerGetServer, { isLoading: isLoadingGetServer }] = useLazyGetServerQuery();

  const [
    triggerPostBank,
    {
      isSuccess: isSuccessPostBank,
      isLoading: isLoadingPostBank,
      isError: isErrorPostBank,
      error: errorPostBank,
      reset: resetPostBank,
    },
  ] = usePostBankMutation();

  const [
    triggerPutBank,
    {
      isSuccess: isSuccessPutBank,
      isError: isErrorPutBank,
      error: errorPutBank,
      isLoading: isLoadingPutBank,
      reset: resetPutBank,
    },
  ] = usePutBankMutation();

  const [
    triggerPostLift,
    { isSuccess: isSuccessPostLift, isError: isErrorPostLift, reset: resetPostLift },
  ] = usePostLiftMutation();

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const createAutomaticallyLifts = async (
    bankId: string,
    bankType: string,
    numberOfLifts: number,
  ) => {
    const token = await GETJwtToken();
    const promises = [];
    for (let i = 1; i <= numberOfLifts; i += 1) {
      const BODY = {
        token,
        body: {
          bank_id: bankId,
          offsets: [],
          type: bankType,
          name: `Car ${i}`,
          position: i,
          direction: "",
          control_panel: JSON.stringify({
            carcall: {
              code: "cc",
              map: [
                {
                  offset: "3c",
                  target_bit: "01",
                  new_bit: "10",
                },
              ],
            },
            rear_carcall: {
              code: "rcc",
              map: [
                {
                  offset: "3c",
                  new_byte: "1a",
                },
              ],
            },
            close_door: {
              code: "cd",
              map: [
                {
                  offset: "3c",
                  target_bit: "01",
                  new_bit: "10",
                },
              ],
            },
            open_door: {
              code: "od",
              map: [
                {
                  offset: "3c",
                  new_byte: "1a",
                },
              ],
            },
            hall_call: {
              code: "hc",
              map: [
                {
                  offset: "3c",
                  target_bit: "01",
                  new_bit: "10",
                },
              ],
            },
            secure: {
              code: "sec",
              map: [
                {
                  offset: "3c",
                  new_byte: "10",
                },
              ],
            },
          }),
        },
      };
      promises.push(triggerPostLift(BODY).unwrap());
    }
  };

  const onClickSave = async () => {
    const token = await GETJwtToken();
    const BODY: any = { ...bank };
    // Per-bank MCE event-reporting address goes on the wire as snake_case.
    // The state can carry either spelling (camelCase from this form, snake_case
    // when the bank arrived from the JSON API path), so normalize both. Empty
    // or whitespace-only means UNSET — omit the key so the payload pointer
    // lands nil and the Go side clears the column (same trim-and-omit
    // behavior as the wizard's StepBanks).
    const eraRaw =
      typeof BODY.eventReportingAddress === "string"
        ? BODY.eventReportingAddress
        : BODY.event_reporting_address;
    const era = typeof eraRaw === "string" ? eraRaw.trim() : "";
    delete BODY.eventReportingAddress;
    delete BODY.event_reporting_address;
    if (era !== "") {
      BODY.event_reporting_address = era;
    }

    // OPC vendor path segments: same dual-spelling normalization (a bank that
    // arrived over the JSON API carries snake_case). For an OPC-addressed bank
    // this form is the authoritative editor — it always sends all three (empty
    // string for a cleared input) and flags the set as provided, so the server
    // clears a blank segment to NULL. Any other protocol keeps the trim-and-omit
    // behaviour and sends no flag, so the guarded write leaves stored columns
    // untouched.
    OPC_VENDOR_PATH_FIELDS.forEach((field: OpcVendorPathField) => {
      const camel = OPC_VENDOR_PATH_CAMEL[field];
      const raw = typeof BODY[camel] === "string" ? BODY[camel] : BODY[field];
      const trimmed = typeof raw === "string" ? raw.trim() : "";
      delete BODY[camel];
      delete BODY[field];
      if (isOpcPathProto || trimmed !== "") {
        BODY[field] = trimmed;
      }
    });
    if (isOpcPathProto) {
      BODY.vendor_paths_provided = true;
    }
    if (
      channelType === ChannelType.Serial ||
      channelType === ChannelType.Tcp ||
      channelType === ChannelType.TcpBot
    ) {
      delete BODY.adapterId;
    }
    if (channelType === ChannelType.Adapter) {
      delete BODY.configurationId;
    }
    if (formAction === FormAction.Create) {
      const copy = { ...BODY };
      copy.floorCount = copy.stops;
      const response = await triggerPostBank({ token, body: copy }).unwrap();
      if (response && response.data && response.data.id) {
        createAutomaticallyLifts(
          formatUuidT(response.data.id),
          liftTypeLabel(BODY.type),
          BODY.numberOfLifts,
        );
      }
    } else {
      const copy = { ...BODY };
      delete copy.dataBlocks;
      copy.lifts = [];
      if (channelType === ChannelType.Adapter && offsetMatrixValues.length > 0) {
        copy.offsetMapping = offsetMatrixValues;
      }
      triggerPutBank({ token, body: copy, id: formatUuidT(bank.id) });
    }
  };

  const onChangeShowLoading = () => {
    setShowLoading(true);
    setTimeout(() => {
      setShowLoading(false);
    }, 300);
  };

  const resetData = () => {
    setBank({ ...BLANK_BANK });
    setIdOrganization("");
    setChannels([]);
    setChannelType("");
    setDataBlocks([]);
    setFloorsAndDirectionData(BLANK_FLOORS_DIRECTION);
    resetPostBank();
    resetPutBank();
    resetPostLift();
    dispatch(updateBank({ ...BLANK_BANK }));
  };

  const onClickClose = () => {
    resetData();
    if (onCancel) {
      onCancel();
    } else {
      dispatch(updateOpenModalCreateEditBank(false));
    }
  };

  const onChangeFormValue = (newValue: string | number, variableName: string) => {
    const copy: BankInterface = { ...bank };
    if (variableName === "name" && typeof newValue === "string") {
      copy.name = newValue;
    } else if (variableName === "type" && typeof newValue === "number") {
      copy.type = newValue as LiftType;
    } else if (variableName === "stops" && typeof newValue === "number") {
      copy.stops = newValue;
    } else if (variableName === "floorCount" && typeof newValue === "number") {
      copy.floorCount = newValue;
    } else if (variableName === "numberOfLifts" && typeof newValue === "number") {
      copy.numberOfLifts = newValue;
    } else if (variableName === "channelId" && typeof newValue === "string") {
      (copy as any).channelId = newValue;
    } else if (variableName === "logicalPort" && typeof newValue === "number") {
      copy.logicalPort = newValue;
    } else if (variableName === "eventReportingAddress" && typeof newValue === "string") {
      copy.eventReportingAddress = newValue;
    } else if (
      variableName === OPC_VENDOR_PATH_CAMEL.vendor_location_path &&
      typeof newValue === "string"
    ) {
      copy.vendorLocationPath = newValue;
    } else if (
      variableName === OPC_VENDOR_PATH_CAMEL.vendor_site_path &&
      typeof newValue === "string"
    ) {
      copy.vendorSitePath = newValue;
    } else if (
      variableName === OPC_VENDOR_PATH_CAMEL.vendor_group_path &&
      typeof newValue === "string"
    ) {
      copy.vendorGroupPath = newValue;
    }
    setBank(copy);
  };

  const onChangeIdOrganization = (newValue: string) => {
    setIdOrganization(newValue);
    const copy: BankInterface = { ...bank, serverId: "", channelId: null };
    setBank(copy);
  };

  const onChangeServer = (server_: any) => {
    const copy: BankInterface = { ...bank, serverId: server_.id, channelId: null };
    setBank(copy);
    if (server_.channels) {
      setChannels(server_.channels);
    }
  };

  const fillEditForm = () => {
    const copy: BankInterface = { ...selectedBank };
    // The API surfaces the MCE reporting address as snake_case on the wire and
    // camelCase via the FB Object API — accept both, keep the raw value (the
    // trim happens on save, same as the wizard's StepBanks).
    const eraRaw =
      (selectedBank as any)?.event_reporting_address ?? selectedBank?.eventReportingAddress ?? null;
    copy.eventReportingAddress = typeof eraRaw === "string" ? eraRaw : null;
    if (copy && copy.channel && (copy.channel as any).server && (copy.channel as any).server.id) {
      copy.serverId = (copy.channel as any).server.id;
      setBank(copy);
    }
    if (copy && copy.channel && (copy.channel as any).type) {
      setChannelType((copy.channel as any).type);
    }
    if (formAction === FormAction.Edit && selectedBank && selectedBank.adapterId) {
      const copy2 = { ...floorsAndDirectionData };
      copy2.adapterId = formatUuidT(selectedBank.adapterId);
      setFloorsAndDirectionData(copy2);
    }
    if (
      copy &&
      copy.channel &&
      (copy.channel as any).server &&
      (copy.channel as any).server.org &&
      (copy.channel as any).server.org.id
    ) {
      setIdOrganization((copy.channel as any).server.org.id);
    }
    if (
      copy &&
      copy.channel &&
      (copy.channel as any).type === ChannelType.Adapter &&
      copy.adapter
    ) {
      const adap = copy.adapter as any;
      const cdoData = adap.cdo_template || adap.cdo_input;
      const hasIntra = Boolean(
        adap.intra_content_flags &&
        typeof adap.intra_content_flags === "object" &&
        Object.values(adap.intra_content_flags).some((arr: unknown) =>
          Array.isArray(arr) ? (arr as boolean[]).some(Boolean) : Boolean(arr),
        ),
      );
      const types = extractMappedMessageTypes(cdoData, adap, hasIntra);
      setOffsetMatrixValues(parseOffsetMapping(copy.offsetMapping, types));
    } else {
      setOffsetMatrixValues([]);
    }
  };

  const disableSaveButton = () => {
    if (!bank.name || !bank.channelId || !bank.serverId) {
      return true;
    }
    if (offsetMatrixHasOverlaps) {
      return true;
    }
    if (opcVendorPathVerdict === "block") {
      return true;
    }
    return false;
  };

  const getServerById = async (serverId?: string) => {
    const token = await GETJwtToken();
    const idToFetch = serverId ?? idServer;
    const response = await triggerGetServer({ token, idServer: idToFetch }).unwrap();
    if (response && response.data) {
      const copy: BankInterface = { ...bank, serverId: response.data.id };
      if (response.data.org && response.data.org.id) {
        setIdOrganization(response.data.org.id);
      }
      setBank(copy);
      if (response.data.channels) {
        setChannels(response.data.channels);
      }
    }
  };

  // ************************************************* */
  // USE EFFECT ************************************** */
  useEffect(() => {
    if (!openModalCreateEditBank) {
      return;
    }
    onChangeShowLoading();
    if (formAction === FormAction.Edit) {
      fillEditForm();
    } else if (idServer && idServer !== "") {
      getServerById();
    } else {
      if (selectedBank && selectedBank.serverId && selectedBank.serverId !== "") {
        const copy: BankInterface = { ...bank, serverId: selectedBank.serverId };
        setBank(copy);
      }
      if (
        userLogged &&
        userLogged.organizationId &&
        userLogged.organizationId === globalOrganizationId
      ) {
        setIdOrganization("");
      } else {
        setIdOrganization(globalOrganizationId);
      }
    }
  }, [openModalCreateEditBank]);

  useEffect(() => {
    if (
      formAction === FormAction.Create &&
      bank.serverId &&
      bank.serverId !== "" &&
      channels.length === 0
    ) {
      getServerById(bank.serverId);
    }
  }, [bank.serverId, formAction, channels.length]);

  useEffect(() => {
    const channelIdStr = bank.channelId
      ? String((bank.channelId as any).value ?? bank.channelId)
      : "";
    if (channelIdStr !== "") {
      const index = channels.findIndex((x: any) => x.id === channelIdStr);
      if (index >= 0) {
        setChannelType(String(channels[index].type));
      }
    }
  }, [bank.channelId]);

  useEffect(() => {
    if (dataBlocks) {
      const copy: BankInterface = { ...bank, dataBlocks };
      setBank(copy);
    }
  }, [dataBlocks]);

  useEffect(() => {
    if (floorsAndDirectionData) {
      if (channelType !== ChannelType.Adapter) {
        const copy: BankInterface = {
          ...bank,
          floorBelowLobby: floorsAndDirectionData.numberOfFloorsBelowLobby,
          stops: floorsAndDirectionData.numberOfStops,
          numberOfLifts: floorsAndDirectionData.numberOfLifts,
          configurationId: floorsAndDirectionData.configurationId,
        };
        setBank(copy);
      } else {
        const copy: BankInterface = {
          ...bank,
          floorBelowLobby: floorsAndDirectionData.numberOfFloorsBelowLobby,
          stops: floorsAndDirectionData.numberOfStops,
          numberOfLifts: floorsAndDirectionData.numberOfLifts,
          adapterId: floorsAndDirectionData.adapterId as any,
        };
        setBank(copy);
      }
    }
  }, [floorsAndDirectionData]);

  useEffect(() => {
    const anyChange =
      isSuccessPostBank ||
      isErrorPostBank ||
      isSuccessPutBank ||
      isErrorPutBank ||
      isSuccessPostLift ||
      isErrorPostLift;
    if (!anyChange) {
      return;
    }
    if (mode === "page") {
      if (isSuccessPostBank || isSuccessPutBank || isSuccessPostLift) {
        resetData();
        if (onSaveSuccess) {
          onSaveSuccess();
        }
      }
    } else {
      onClickClose();
    }
  }, [
    isSuccessPostBank,
    isErrorPostBank,
    isSuccessPutBank,
    isErrorPutBank,
    isSuccessPostLift,
    isErrorPostLift,
  ]);

  // ************************************************ */
  // COMPONENT ************************************** */

  // Derive offset matrix props when editing an ADAPTER bank.
  // Computed here (not inline IIFE) so React can track the derived values.
  const adapterOffsetMatrixNode: React.ReactNode = (() => {
    if (channelType !== ChannelType.Adapter || formAction !== FormAction.Edit) return null;
    const adap = (selectedBank as any)?.adapter;
    if (!adap) return null;
    const cdoData = adap.cdo_template || adap.cdo_input;
    const hasIntra = Boolean(
      adap.intra_content_flags &&
      typeof adap.intra_content_flags === "object" &&
      Object.values(adap.intra_content_flags).some((arr: unknown) =>
        Array.isArray(arr) ? (arr as boolean[]).some(Boolean) : Boolean(arr),
      ),
    );
    const mappedTypes = extractMappedMessageTypes(cdoData, adap, hasIntra) ?? [];
    if (mappedTypes.length === 0) return null;
    const adapterKeyInfos = extractAdapterKeys(adap);
    // For an LN1-family adapter extractAdapterKeys returns [] on purpose, so
    // the matrix falls back to range-based detection. That fallback can excuse
    // a Control/Traffic pair sharing one byte in OPPOSITE directions, but only
    // if it is given the directions -- which is the whole reason this screen
    // and the adapter screen used to disagree about the same keys.
    const ln1KeyMappingsForMatrix = deserializeApiKeys((adap as any)?.keys ?? []);
    const liftList: { id: string; name: string }[] =
      selectedBank.lifts && selectedBank.lifts.length > 0
        ? selectedBank.lifts.map((l: any) => ({ id: l.id, name: l.name }))
        : Array.from({ length: selectedBank.numberOfLifts || 0 }, (_, i) => ({
            id: String(i),
            name: `Car ${i + 1}`,
          }));
    return (
      <BankOffsetMatrix
        lifts={liftList}
        mappedTypes={mappedTypes}
        offsets={offsetMatrixValues}
        onOffsetsChange={setOffsetMatrixValues}
        onOverlapChange={setOffsetMatrixHasOverlaps}
        floorCount={selectedBank.floorCount ?? selectedBank.stops}
        intraContent={false}
        adapterKeys={adapterKeyInfos}
        ln1KeyMappings={ln1KeyMappingsForMatrix}
        intraContentFlags={(adap as any)?.intra_content_flags ?? undefined}
      />
    );
  })();

  // Per-bank MCE event/fault-reporting address input — MCE-only, same as the
  // wizard's Banks stage. In EDIT mode the embedded adapter carries the
  // protocol; in CREATE mode it arrives via floorsAndDirectionData from
  // AdaptersAssignAdapter once the adapter is picked.
  const assignedAdapterProtocol = (() => {
    if (formAction === FormAction.Edit && selectedBank) {
      const proto = resolveAdapterProtocolUpper((selectedBank as any)?.adapter);
      if (proto !== "") return proto;
    }
    return resolveAdapterProtocolUpper({
      protocol_type: floorsAndDirectionData.adapterProtocol,
    });
  })();
  const isMceProto = assignedAdapterProtocol === ProtocolType.Mce;
  // Both OPC dialects address points by a dotted node path whose upper segments
  // vary per deployment; the adapter holds only the leaf names.
  const isOpcPathProto = OPC_PATH_PROTOCOLS.has(assignedAdapterProtocol);
  // The partial-state rule compares the three segments against the bank as it
  // was loaded; a bank being created has no such baseline, so it can never start
  // out partial. `selectedBank` is the slice's copy and is not edited by this
  // form, which works on its own `bank` state.
  const initialOpcVendorPaths =
    formAction === FormAction.Edit ? readOpcVendorPathValues(selectedBank) : null;
  const opcVendorPathValues = readOpcVendorPathValues(bank);
  const opcVendorPathVerdict = isOpcPathProto
    ? opcVendorPathTransition(initialOpcVendorPaths, opcVendorPathValues)
    : "ok";
  const opcVendorPathPreview = isOpcPathProto
    ? composeOpcUaNodePreview(
        opcVendorPathValues,
        formAction === FormAction.Edit ? firstLiftVendorPath(selectedBank?.lifts) : null,
      )
    : null;
  // The port must fit what the assigned adapter's protocol can carry (LobbyVision2
  // stamps it into a uint8 bank number; everything is capped by the schema's
  // int16). Before an adapter is assigned the protocol is "" and only the int16
  // cap applies. Blocks the save below rather than letting the server refuse it.
  const logicalPortInvalid = logicalPortOutOfRange(assignedAdapterProtocol, bank.logicalPort);

  const formFooter = (
    <div className="container generalStyles__flex mt-4">
      <div className="generalStyles__width100 generalStyles__mrFix">
        <ButtonClose onClick={onClickClose} />
      </div>
      <div className="generalStyles__width100 generalStyles__mlFix">
        <ButtonSave
          onClick={onClickSave}
          disabledButton={disableSaveButton() || logicalPortInvalid}
          isLoading={false}
        />
      </div>
    </div>
  );

  const formContent = (
    <Spin spinning={showLoading || isLoadingPostBank || isLoadingPutBank || isLoadingGetServer}>
      {/** ************************************************************* */}
      {/** DIVIDER BANK */}
      <DrawerRowDivider
        icon={faBuilding}
        title={
          formAction === FormAction.Create
            ? t("edgeManagement.banks.createNewBank")
            : t("edgeManagement.banks.editBank")
        }
      />
      <div className={`drawer__box__${theme}`}>
        {/** NAME */}
        <div className="generalStyles__flex drawer__container">
          <div>
            <p className="drawer__title">
              {" "}
              <span className="generalStyles__inputFlexRequired">*</span>
              {t("general.name")}
            </p>
          </div>
          <div className="generalStyles__width100 drawer__textContainer generalStyles__flex">
            <Input
              size="small"
              onChange={(value) => {
                onChangeFormValue(value.target.value, "name");
              }}
              value={String(bank.name ?? "")}
            />
            <div className="generalStyles__infoTooltipIconBlank" />
          </div>
        </div>
        {/** TYPE */}
        <div className="generalStyles__flex drawer__container mt-2">
          <div>
            <p className="drawer__title">
              {" "}
              <span className="generalStyles__inputFlexRequired">*</span>
              {t("general.type")}
            </p>
          </div>
          <div className="generalStyles__width100 drawer__textContainer generalStyles__flex">
            <Select
              value={bank.type}
              onChange={(value) => {
                onChangeFormValue(value, "type");
              }}
              className="generalStyles__width100"
              size="small"
              disabled={formAction === FormAction.Edit}
            >
              {[
                LiftType.ELEVATOR,
                LiftType.ESCALATOR,
                LiftType.MOVINGWALK,
                LiftType.BOT,
                LiftType.GPIO,
              ].map((item: LiftType) => (
                <Option key={item} value={item}>
                  {liftTypeLabel(item)}
                </Option>
              ))}
            </Select>
            <div className="generalStyles__infoTooltipIconBlank" />
          </div>
        </div>
        {/** LOGICAL PORT */}
        <div className="generalStyles__flex drawer__container mt-2">
          <div>
            <p className="drawer__title">
              <span className="generalStyles__inputFlexRequired">*</span>
              {t("general.logicalPort")}
            </p>
          </div>
          <div className="generalStyles__width100 drawer__textContainer generalStyles__flex">
            <InputNumber
              className="generalStyles__inputNumberFull"
              precision={0}
              value={bank.logicalPort}
              onChange={(e: any) => {
                onChangeFormValue(e, "logicalPort");
              }}
              max={logicalPortMax(assignedAdapterProtocol)}
              status={logicalPortInvalid ? "error" : undefined}
              size="small"
            />
            <div className="generalStyles__infoTooltipIconBlank" />
          </div>
        </div>
        {logicalPortInvalid && (
          <p className="generalStyles__error" role="alert" data-testid="logical-port-error">
            {t("edgeManagement.banks.logicalPortOutOfRange", {
              min: logicalPortMin(assignedAdapterProtocol),
              max: logicalPortMax(assignedAdapterProtocol),
            })}
          </p>
        )}
        {/** EVENT REPORTING ADDRESS — MCE adapters only */}
        {isMceProto && (
          <div className="generalStyles__flex drawer__container mt-2">
            <div>
              <p className="drawer__title">Event Reporting Address</p>
            </div>
            <div className="generalStyles__width100 drawer__textContainer generalStyles__flex">
              <Input
                size="small"
                onChange={(value) => {
                  onChangeFormValue(value.target.value, "eventReportingAddress");
                }}
                value={String(bank.eventReportingAddress ?? "")}
              />
              <div className="generalStyles__infoTooltipIconBlank" />
            </div>
          </div>
        )}
        {/** OPC VENDOR PATH SEGMENTS — OPC DA and OPC UA adapters only */}
        {isOpcPathProto &&
          OPC_VENDOR_PATH_FIELDS.map((field: OpcVendorPathField) => (
            <div className="generalStyles__flex drawer__container mt-2" key={field}>
              <div>
                <p className="drawer__title">{OPC_VENDOR_PATH_LABELS[field]}</p>
              </div>
              <div className="generalStyles__width100 drawer__textContainer generalStyles__flex">
                <Input
                  size="small"
                  onChange={(value) => {
                    onChangeFormValue(value.target.value, OPC_VENDOR_PATH_CAMEL[field]);
                  }}
                  value={opcVendorPathValues[field] ?? ""}
                />
                <div className="generalStyles__infoTooltipIconBlank" />
              </div>
            </div>
          ))}
        {isOpcPathProto && (
          <OpcVendorPathFeedback transition={opcVendorPathVerdict} preview={opcVendorPathPreview} />
        )}
        {formAction === FormAction.Edit && (
          <>
            {/** STOPS */}
            <div className="generalStyles__flex drawer__container mt-2">
              <div>
                <p className="drawer__title">
                  <span className="generalStyles__inputFlexRequired">*</span>
                  {t("configuration.configurationFiles.stops")}
                </p>
              </div>
              <div className="generalStyles__width100 drawer__textContainer generalStyles__flex">
                <InputNumber
                  className="generalStyles__inputNumberFull"
                  precision={0}
                  value={bank.stops}
                  onChange={(e: any) => {
                    onChangeFormValue(e, "stops");
                  }}
                  max={999}
                  size="small"
                />
                <div className="generalStyles__infoTooltipIconBlank" />
              </div>
            </div>
            {/** FLOOR COUNT */}
            <div className="generalStyles__flex drawer__container mt-2">
              <div>
                <p className="drawer__title">
                  <span className="generalStyles__inputFlexRequired">*</span>
                  {t("configuration.configurationFiles.floors")}
                </p>
              </div>
              <div className="generalStyles__width100 drawer__textContainer generalStyles__flex">
                <InputNumber
                  className="generalStyles__inputNumberFull"
                  precision={0}
                  value={bank.floorCount}
                  onChange={(e: any) => {
                    onChangeFormValue(e, "floorCount");
                  }}
                  max={999}
                  size="small"
                />
                <div className="generalStyles__infoTooltipIconBlank" />
              </div>
            </div>
            {/** NUMBER OF LIFTS */}
            <div className="generalStyles__flex drawer__container mt-2">
              <div>
                <p className="drawer__title">
                  <span className="generalStyles__inputFlexRequired">*</span>
                  Units
                </p>
              </div>
              <div className="generalStyles__width100 drawer__textContainer generalStyles__flex">
                <InputNumber
                  className="generalStyles__inputNumberFull"
                  precision={0}
                  value={bank.numberOfLifts}
                  onChange={(e: any) => {
                    onChangeFormValue(e, "numberOfLifts");
                  }}
                  max={999}
                  size="small"
                />
                <div className="generalStyles__infoTooltipIconBlank" />
              </div>
            </div>
          </>
        )}
      </div>
      <br />
      {/** ************************************************************* */}
      {/** DIVIDER BANK CONFIGURATION */}
      <DrawerRowDivider icon={faEthernet} title={t("general.channel")} />
      <div className={`drawer__box__${theme}`}>
        {/** ORGANIZATION */}
        <div className="generalStyles__flex drawer__container mt-3">
          <div>
            <p className="drawer__title">
              {" "}
              <span className="generalStyles__inputFlexRequired">*</span>
              {t("userManagement.organization")}
            </p>
          </div>
          <div className="generalStyles__width100 drawer__textContainer generalStyles__flex">
            <SelectOrganization
              onChangeIdOrganization={onChangeIdOrganization}
              value={idOrganization}
              disabledInput={
                (userLogged &&
                  userLogged.organizationId &&
                  userLogged.organizationId !== globalOrganizationId) ||
                (idServer && idServer !== "") ||
                formAction === FormAction.Edit
              }
            />
            <div className="generalStyles__infoTooltipIconBlank" />
          </div>
        </div>
        {/** SERVER */}
        <div className="generalStyles__flex drawer__container mt-2">
          <div>
            <p className="drawer__title">
              <span className="generalStyles__inputFlexRequired">*</span>
              {t("edgeManagement.servers.server")}
            </p>
          </div>
          <div className="generalStyles__width100 drawer__textContainer generalStyles__flex">
            <SelectServerByOrganization
              onChangeServer={onChangeServer}
              value={bank.serverId ?? ""}
              idOrganization={idOrganization}
              disabledInput={(idServer && idServer !== "") || formAction === FormAction.Edit}
            />
            <div className="generalStyles__infoTooltipIconBlank">
              {!bank.serverId && idOrganization === "" && (
                <Tooltip title={t("tooltip.organizationRequired")} placement="left">
                  <FontAwesomeIcon icon={faCircleInfo} className="generalStyles__infoTooltipIcon" />
                </Tooltip>
              )}
            </div>
          </div>
        </div>
        {/** CHANNEL */}
        <div className="generalStyles__flex drawer__container mt-2">
          <div>
            <p className="drawer__title">
              <span className="generalStyles__inputFlexRequired">*</span>
              {t("edgeManagement.channels.channel")}
            </p>
          </div>
          <div className="generalStyles__width100 drawer__textContainer generalStyles__flex">
            {formAction === FormAction.Create ? (
              <Select
                value={bank.channelId as any}
                onChange={(value) => {
                  onChangeFormValue(value, "channelId");
                }}
                className="generalStyles__width100"
                size="small"
                disabled={!bank.serverId}
                showSearch
                filterOption={(input, option) => {
                  const channel = channels.find((ch) => ch.id === option?.value);
                  if (!channel) return false;
                  const searchText = `${channel.type} ${channel.name}`.toLowerCase();
                  return searchText.includes(input.toLowerCase());
                }}
                virtual
                listHeight={256}
              >
                {channels.map((item: ChannelInterface) => (
                  <Option key={String(item.id)} value={String(item.id)}>
                    <span className="generalStyles__info">{item.type}</span>{" "}
                    <span>{String(item.name ?? "")}</span>{" "}
                  </Option>
                ))}
              </Select>
            ) : (
              <Input
                size="small"
                disabled
                value={
                  selectedBank && selectedBank.channel && (selectedBank.channel as any).name
                    ? (selectedBank.channel as any).name
                    : "--"
                }
              />
            )}
            <div className="generalStyles__infoTooltipIconBlank">
              {!bank.channelId && (
                <Tooltip title={t("tooltip.serverRequired")} placement="left">
                  <FontAwesomeIcon icon={faCircleInfo} className="generalStyles__infoTooltipIcon" />
                </Tooltip>
              )}
            </div>
          </div>
        </div>
      </div>
      {/** ************************************************************* */}
      {/** DIVIDER ASSIGN ADAPTER */}
      {channelType === ChannelType.Adapter && formAction === FormAction.Edit && (
        <>
          <br />
          <AdaptersAssignAdapter
            liftType={bank.type}
            setFloorsAndDirectionData={setFloorsAndDirectionData}
            formAction="EDIT"
            adapterId={formatUuidT(bank.adapterId) || ""}
            lifts={selectedBank.numberOfLifts}
            stops={selectedBank.stops}
            maxStops={selectedBank.stops}
            floorsBelowLobby={selectedBank.floorBelowLobby}
          />
        </>
      )}
      {/** ************************************************************* */}
      {/** OFFSET MATRIX — only for ADAPTER banks in EDIT mode */}
      {adapterOffsetMatrixNode}
      {/** ************************************************************* */}
      {/** DIVIDER ASSIGN CONFIGURATION */}
      {formAction === FormAction.Create && (
        <>
          {(channelType === ChannelType.Serial || channelType === ChannelType.Tcp) &&
            bank.type !== LiftType.GPIO && (
              <>
                <br />
                <ConfigurationFilesAssignConfiguration
                  setDataBlocks={setDataBlocks}
                  liftType={bank.type}
                  setFloorsAndDirectionData={setFloorsAndDirectionData}
                />
              </>
            )}
          {(channelType === ChannelType.Serial || channelType === ChannelType.Tcp) &&
            bank.type === LiftType.GPIO && (
              <>
                <br />
                <DrawerRowDivider icon={faGears} title={`${t("general.configuration")}`} />
                <div className={`drawer__box__${theme}`}>
                  <Alert
                    message={t("edgeManagement.banks.gpioNotAssignable")}
                    type="warning"
                    showIcon
                    closable
                  />
                </div>
              </>
            )}
          {/** ************************************************************* */}
          {/** DIVIDER ASSIGN ADAPTER */}
          {channelType === ChannelType.Adapter && (
            <>
              <br />
              <AdaptersAssignAdapter
                liftType={bank.type}
                setFloorsAndDirectionData={setFloorsAndDirectionData}
                formAction="CREATE"
                adapterId=""
                lifts={1}
                stops={2}
                maxStops={2}
                floorsBelowLobby={1}
              />
            </>
          )}
          {/** ************************************************************* */}
          {/** DIVIDER ASSIGN ADAPTER OR CONFIGURATION */}
          {channelType === "" && (
            <>
              <br />
              <DrawerRowDivider
                icon={faGears}
                title={`${t("edgeManagement.banks.configurationAndAdapters")}`}
              />
              <div className={`drawer__box__${theme}`}>
                <Alert
                  message={t("edgeManagement.banks.configurationAndAdaptersDescription")}
                  type="warning"
                  showIcon
                  closable={false}
                />
              </div>
            </>
          )}
        </>
      )}
      {/** ************************************************************* */}
      {/** GLOBAL ALERT */}
      <GlobalAlert2
        isError={isErrorPostBank}
        isSuccess={isSuccessPostBank}
        requestType="POST"
        error={errorPostBank}
        name="Bank"
      />
      <GlobalAlert2
        isError={isErrorPutBank}
        isSuccess={isSuccessPutBank}
        requestType="PUT"
        error={errorPutBank}
        name="Bank"
      />
    </Spin>
  );

  if (mode === "page") {
    return (
      <div>
        {formContent}
        {formFooter}
      </div>
    );
  }

  return (
    <Drawer
      width="650"
      placement="right"
      onClose={onClickClose}
      closable={false}
      open={openModalCreateEditBank}
      footer={formFooter}
    >
      {formContent}
    </Drawer>
  );
}

export default BanksCreateEditForm;
